import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReportDetailsModal from '../../components/admin/ReportDetailsModal';
import UserDetailsModal from '../../components/admin/UserDetailsModal';
import AdminDashboard from '../../components/admin/AdminDashboard';
import AdminHeader from '../../components/admin/AdminHeader';
import NavigationTabs from '../../components/admin/NavigationTabs';
import FilterBar from '../../components/admin/FilterBar';
import ResultsSummary from '../../components/admin/ResultsSummary';
import UsersTab from '../../components/admin/UsersTab';
import ReportsTab from '../../components/admin/ReportsTab';
import JobsTab from '../../components/admin/JobsTab';
import AdminAnalytics from '../../components/admin/AdminAnalytics';
import UpgradeAnalyticsDashboard from '../../components/admin/UpgradeAnalyticsDashboard';
import ProfilePictureReviews from '../../components/admin/ProfilePictureReviews';
import AdminJobModeration from './JobModerationTab';
import FeedbackDashboard from '../admin/FeedbackDashboard';
import AdminSupportDashboard from '../admin/support/AdminSupportDashboard';
import HelpArticlesCMS from '../admin/help-cms';
import ArticleEditor from '../admin/help-cms/ArticleEditor';
import MarketingCampaignsTab from './components/MarketingCampaignsTab';
import SkillManagement from '../../components/admin/SkillManagement';
import CreditsManagement from '../admin/credits-management';
import { useFilteredReports, useFilteredUsers, useFilteredJobs } from '../../hooks/useAdminFilters';
import { useAuth } from '../../hooks/useAuth';
import { useAdminActions } from '../../hooks/useAdminActions';
import adminModerationService from '../../services/adminModerationService';
import jobModerationService from '../../services/jobModerationService';
import ChatService from '../../services/chatService';
import { profilePictureService } from '../../utils/profilePictureService';

const AdminModerationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  
  // Get active tab from URL parameters, default to 'dashboard'
  const tabFromURL = searchParams.get('tab') || 'dashboard';
  
  // State management
  const [activeTab, setActiveTab] = useState(tabFromURL);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Data state
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pendingPictureReviews, setPendingPictureReviews] = useState(0);
  const [pendingJobModerations, setPendingJobModerations] = useState(0);
  const [pendingChatsCount, setPendingChatsCount] = useState(0);
  const [pendingSkillModerations, setPendingSkillModerations] = useState(0);
  
  // Modal state
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  // Function to refresh dashboard when actions are performed
  const handleRefreshDashboard = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Custom hooks
  const filteredReports = useFilteredReports(reports, searchTerm, filterStatus, filterRole, filterDateRange);
  const filteredUsers = useFilteredUsers(users, searchTerm, filterStatus, filterRole, filterDateRange, userProfile?.role);
  const filteredJobs = useFilteredJobs(jobs, searchTerm, filterStatus, filterRole, filterDateRange);
  const { handleReportAction, handleUserAction } = useAdminActions(setReports, setUsers, handleRefreshDashboard);

  // Job update handler
  const handleJobUpdate = async () => {
    try {
      const jobsResponse = await adminModerationService.getJobs();
      if (jobsResponse.success) {
        setJobs(jobsResponse.data);
      }
    } catch (error) {
      console.error('Error refreshing jobs:', error);
    }
  };

  // Data loading
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        const [reportsResponse, usersResponse, jobsResponse, reviewsResponse, pendingModerationsResponse, chatsResponse] = await Promise.all([
          adminModerationService.getReports(),
          adminModerationService.getUsers(),
          adminModerationService.getJobs(),
          profilePictureService.getPendingReviews(),
          jobModerationService.getPendingJobModerationsCount(),
          ChatService.getAllConversations()
        ]);

        if (reportsResponse.success) setReports(reportsResponse.data);
        if (usersResponse.success) setUsers(usersResponse.data);
        if (jobsResponse.success) setJobs(jobsResponse.data);
        if (reviewsResponse.success) setPendingPictureReviews(reviewsResponse.data.length);
        if (pendingModerationsResponse.success) setPendingJobModerations(pendingModerationsResponse.count);
        if (chatsResponse.success) {
          const openChats = chatsResponse.data.filter(c => c.status === 'open' || c.status === 'in_progress').length;
          setPendingChatsCount(openChats);
        }
      } catch (error) {
        console.error('Error loading admin data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Sync activeTab with URL parameters
  useEffect(() => {
    const tabFromURL = searchParams.get('tab') || 'dashboard';
    if (tabFromURL !== activeTab) {
      setActiveTab(tabFromURL);
    }
  }, [searchParams, activeTab]);

  // Custom setActiveTab function that updates URL parameters
  const handleSetActiveTab = (tabId) => {
    setActiveTab(tabId);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabId);
    setSearchParams(newSearchParams);
  };

  // Event handlers
  const handleReportSelect = (report) => {
    setSelectedReport(report);
    setIsReportModalOpen(true);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  const closeReportModal = () => {
    setIsReportModalOpen(false);
    setSelectedReport(null);
  };

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setSelectedUser(null);
  };

  // Computed values
  const submittedReportsCount = reports.filter(r => r.status === 'submitted').length;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Left sidebar */}
      <NavigationTabs 
        activeTab={activeTab}
        setActiveTab={handleSetActiveTab}
        reports={reports}
        users={users}
        jobs={jobs}
        pendingPictureReviews={pendingPictureReviews}
        pendingJobModerations={pendingJobModerations}
        pendingChatsCount={pendingChatsCount}
        pendingSkillModerations={pendingSkillModerations}
      />

      {/* Right column: header + scrollable content */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <AdminHeader submittedReportsCount={submittedReportsCount} />

        <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Show FilterBar only for reports, users, and jobs tabs */}
        {(activeTab === 'reports' || activeTab === 'users' || activeTab === 'jobs') && (
          <FilterBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            filterRole={filterRole}
            setFilterRole={setFilterRole}
            filterDateRange={filterDateRange}
            setFilterDateRange={setFilterDateRange}
            viewMode={viewMode}
            setViewMode={setViewMode}
            activeTab={activeTab}
          />
        )}

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <AdminDashboard 
            onQuickAction={handleSetActiveTab} 
            refreshTrigger={refreshTrigger}
          />
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-8">
            <AdminAnalytics reports={reports} users={users} jobs={jobs} />
            <div className="border-t border-gray-200 pt-8">
              <UpgradeAnalyticsDashboard />
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div>
            <ResultsSummary 
              count={filteredReports.length}
              itemType="report"
              isLoading={isLoading}
              viewMode={viewMode}
            />
            <ReportsTab
              filteredReports={filteredReports}
              isLoading={isLoading}
              viewMode={viewMode}
              onReportSelect={handleReportSelect}
              onReportAction={handleReportAction}
            />
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <ResultsSummary 
              count={filteredUsers.length}
              itemType="user"
              isLoading={isLoading}
              viewMode={viewMode}
            />
            <UsersTab
              filteredUsers={filteredUsers}
              isLoading={isLoading}
              viewMode={viewMode}
              onUserSelect={handleUserSelect}
              onUserAction={handleUserAction}
            />
          </div>
        )}

        {activeTab === 'jobs' && (
          <div>
            <ResultsSummary 
              count={filteredJobs.length}
              itemType="job"
              isLoading={isLoading}
              viewMode={viewMode}
            />
            <JobsTab
              filteredJobs={filteredJobs}
              isLoading={isLoading}
              viewMode={viewMode}
              onJobUpdate={handleJobUpdate}
            />
          </div>
        )}

        {activeTab === 'profile-pictures' && (
          <ProfilePictureReviews />
        )}

        {activeTab === 'job-moderation' && (
          <AdminJobModeration />
        )}

        {activeTab === 'feedback' && (
          <FeedbackDashboard />
        )}

        {activeTab === 'support' && (
          <AdminSupportDashboard />
        )}

        {activeTab === 'marketing-campaigns' && (
          <MarketingCampaignsTab />
        )}

        {activeTab === 'help-cms' && (
          searchParams.get('mode') === 'create' || searchParams.get('mode') === 'edit' 
            ? <ArticleEditor />
            : <HelpArticlesCMS />
        )}

        {activeTab === 'user-skills' && (
          <SkillManagement onPendingCountChange={setPendingSkillModerations} />
        )}

        {activeTab === 'ai-credits' && (
          <CreditsManagement />
        )}
        </main>
      </div>

      {/* Modals */}
      <ReportDetailsModal
        report={selectedReport}
        isOpen={isReportModalOpen}
        onClose={closeReportModal}
        onAction={handleReportAction}
      />

      <UserDetailsModal
        user={selectedUser}
        isOpen={isUserModalOpen}
        onClose={closeUserModal}
        onAction={handleUserAction}
      />
    </div>
  );
};

export default AdminModerationPage;

